import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import {
  AdminServerTableCellContext,
  AdminServerTableColumn,
  AdminServerTablePaginationState,
} from '../../models/admin-server-table.model';

type PaginationUiItem = number | 'ellipsis';

@Component({
  selector: 'app-admin-server-table',
  templateUrl: './admin-server-table.component.html',
  styleUrls: ['./admin-server-table.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminServerTableComponent<T> {
  @Input() columns: readonly AdminServerTableColumn<T>[] = [];
  @Input() rows: readonly T[] = [];
  @Input() loading = false;
  @Input() emptyMessage = 'No hay registros para mostrar.';
  @Input() ordering = 'id';
  @Input() summaryNoun = 'registros';
  @Input() pagination!: AdminServerTablePaginationState;
  @Input() tableClass = '';
  @Input() tableWrapperClass = '';
  @Input() rowTrackBy: ((index: number, row: T) => string | number) | null | undefined;

  @Output() orderingChange = new EventEmitter<string>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();

  get hasData(): boolean {
    return this.rows.length > 0;
  }

  get paginationItems(): PaginationUiItem[] {
    if (!this.pagination || this.pagination.totalPages <= 0) {
      return [1];
    }
    const total = this.pagination.totalPages;
    const current = this.pagination.page;

    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    let start = Math.max(2, current - 1);
    let end = Math.min(total - 1, current + 1);

    if (current <= 4) {
      start = 2;
      end = 5;
    } else if (current >= total - 3) {
      start = total - 4;
      end = total - 1;
    }

    const items: PaginationUiItem[] = [1];
    if (start > 2) {
      items.push('ellipsis');
    }
    for (let page = start; page <= end; page += 1) {
      items.push(page);
    }
    if (end < total - 1) {
      items.push('ellipsis');
    }
    items.push(total);
    return items;
  }

  onToggleSort(column: AdminServerTableColumn<T>): void {
    if (!column.sortable || !column.sortKey) {
      return;
    }
    const ascValue = column.sortKey;
    const descValue = `-${column.sortKey}`;
    const next = this.ordering === ascValue ? descValue : ascValue;
    this.orderingChange.emit(next);
  }

  sortLabel(column: AdminServerTableColumn<T>): string {
    if (!column.sortable || !column.sortKey) {
      return '';
    }
    if (this.ordering === column.sortKey) {
      return '▲';
    }
    if (this.ordering === `-${column.sortKey}`) {
      return '▼';
    }
    return '↕';
  }

  getCellValue(row: T, column: AdminServerTableColumn<T>): unknown {
    if (column.value) {
      return column.value(row);
    }
    const asRecord = row as unknown as Record<string, unknown>;
    return asRecord[column.id];
  }

  getCellDisplay(row: T, column: AdminServerTableColumn<T>): string {
    const value = this.getCellValue(row, column);
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    return String(value);
  }

  getCellTitle(row: T, column: AdminServerTableColumn<T>): string {
    if (column.title) {
      return column.title(row);
    }
    return this.getCellDisplay(row, column);
  }

  getCellTemplateContext(row: T, column: AdminServerTableColumn<T>): AdminServerTableCellContext<T> {
    const value = this.getCellValue(row, column);
    return {
      $implicit: row,
      row,
      column,
      value,
    };
  }

  onPageSizeChange(value: string | number): void {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return;
    }
    this.pageSizeChange.emit(Math.trunc(numeric));
  }

  goToFirstPage(): void {
    if (this.loading || !this.pagination.hasPreviousPage) {
      return;
    }
    this.pageChange.emit(1);
  }

  goToPreviousPage(): void {
    if (this.loading || !this.pagination.hasPreviousPage) {
      return;
    }
    this.pageChange.emit(this.pagination.page - 1);
  }

  goToNextPage(): void {
    if (this.loading || !this.pagination.hasNextPage) {
      return;
    }
    this.pageChange.emit(this.pagination.page + 1);
  }

  goToLastPage(): void {
    if (this.loading || !this.pagination.hasNextPage) {
      return;
    }
    this.pageChange.emit(this.pagination.totalPages);
  }

  goToPage(page: number): void {
    if (this.loading || page === this.pagination.page) {
      return;
    }
    this.pageChange.emit(page);
  }

  trackByColumn(_: number, column: AdminServerTableColumn<T>): string {
    return column.id;
  }

  trackByRowInternal(index: number, row: T): string | number {
    if (typeof this.rowTrackBy !== 'function') {
      return index;
    }
    try {
      return this.rowTrackBy(index, row);
    } catch {
      return index;
    }
  }

  trackByPaginationItem(index: number, item: PaginationUiItem): string {
    return `${item}-${index}`;
  }
}
